/**
 * Make your character: the wall display's builder, brought across whole.
 *
 * A port of apps/display/public/builder.html, builder.js and builder.css --
 * the grove behind it, the portrait window, the painted child breathing and
 * blinking in it, "Surprise me", the preview of each activity the valley might
 * give them, the four-across part tabs, head thumbnails for hair and extras,
 * garment glyphs for clothes, round swatches for colours, and the name card.
 * The markup and the words are the display's; the class names are prefixed
 * `vb-` so the styles cannot leak into the rest of the app.
 *
 * It covers the whole screen, as the display's page does, wherever it is
 * opened from: the child's page, the full valley, or a parent's Family tab.
 * Those differ only in what saving does, which is `onSave` and the words on
 * one button -- a child's look goes to a grown-up; a parent's is published.
 *
 * The activity picker only PREVIEWS. The valley deals activities out every
 * half-day (see activities.ts); nothing chosen there is saved.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PICTURES } from '../valley/art.generated';
import { assetUrl } from '../valley/sprites';
import { BY_ID, PAINTED } from './activities';
import { catalogue, counts, normalise, outfitIcon, random, type AvatarSpec } from './avatar';
import { renderHead, startMotion } from './painted';
import { usePaintedSvg } from './painting';
import './builder.css';

const ORDER = ['outfit', 'shirtColor', 'hair', 'hairColor', 'skin', 'eyeColor', 'extra'] as const;
type Part = (typeof ORDER)[number];

const WORDS: Record<Part, string> = {
  outfit: 'Clothes',
  shirtColor: 'Clothes colour',
  hair: 'Hair',
  hairColor: 'Hair colour',
  skin: 'Skin',
  eyeColor: 'Eye colour',
  extra: 'Extras',
};

/** Groups whose thumbnail is a head rather than a colour chip. */
const HEAD_PREVIEW = new Set<Part>(['hair', 'extra']);

const PREVIEWS = ['standing', ...PAINTED];

/** Where the display's builder starts a new character. */
export function starterLook(): AvatarSpec {
  return { ...normalise({}), hair: 1, extra: 0, shirtColor: 10, outfit: 0 };
}

const same = (a: AvatarSpec, b: AvatarSpec) => ORDER.every((key) => a[key] === b[key]);

export function Builder({
  initial,
  name,
  saveLabel,
  busy = false,
  error = '',
  note,
  backLabel = 'Back to the valley',
  onSave,
  onCancel,
}: {
  /** The look to start from -- a waiting one if there is one. */
  initial: AvatarSpec | null;
  name: string;
  saveLabel: string;
  busy?: boolean;
  error?: string;
  /** Under the save button: what saving will actually do. */
  note: string;
  backLabel?: string;
  onSave: (spec: AvatarSpec, name: string) => void;
  onCancel: () => void;
}) {
  const [spec, setSpec] = useState<AvatarSpec>(() => (initial ? normalise(initial) : starterLook()));
  const [group, setGroup] = useState<Part>('hair');
  const [who, setWho] = useState(name);
  const [activity, setActivity] = useState('standing');
  const [paused, setPaused] = useState(false);
  const [say, setSay] = useState<{ text: string; bad?: boolean }>({ text: '' });
  const nameField = useRef<HTMLInputElement>(null);
  const back = useRef<HTMLButtonElement>(null);
  const cancel = useRef(onCancel);
  cancel.current = onCancel;

  // The one clock that breathes and blinks every painted child on the page.
  useEffect(() => startMotion(), []);

  // The page underneath should not scroll while this covers it, and Escape
  // is the way back, as it is from every other full-screen view here.
  useEffect(() => {
    const before = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    back.current?.focus();
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancel.current();
    };
    window.addEventListener('keydown', key);
    return () => {
      document.body.style.overflow = before;
      window.removeEventListener('keydown', key);
    };
  }, []);

  useEffect(() => {
    if (error) setSay({ text: error, bad: true });
  }, [error]);

  // Rendered strings, built only from small integers and fixed shapes. The
  // one piece of text, the title, is escaped by `render`. See Character.tsx.
  const preview = usePaintedSvg(spec, { size: 224, title: 'Your character', activity });
  const entry = catalogue[group]!;
  const choices = useMemo(() => {
    if (entry.swatches) return null;
    return (entry.options ?? []).map((label, i) => ({
      label,
      art:
        group === 'outfit'
          ? outfitIcon(i, catalogue.shirtColor!.swatches![spec.shirtColor]!, 56)
          : HEAD_PREVIEW.has(group)
            ? renderHead({ ...spec, [group]: i }, { size: 72, crop: 'hair' })
            : '',
    }));
  }, [entry, group, spec]);

  const current = initial ? normalise(initial) : null;

  function send(): void {
    if (busy) return;
    const trimmed = who.trim();
    if (!trimmed) {
      setSay({ text: 'Put a name in first.', bad: true });
      nameField.current?.focus();
      return;
    }
    setSay({ text: 'Sending…' });
    onSave(spec, trimmed);
  }

  const grove = PICTURES['builder-grove'];

  // Into <body>, not in place (2026-10-13): opened from a child's page it sat
  // inside a frosted panel, and `backdrop-filter` there makes a containing
  // block for `position: fixed` -- the builder was trapped in a strip.
  return createPortal(
    <div className="vb" role="dialog" aria-modal="true" aria-labelledby="vb-title">
      <picture className="vb-grove" aria-hidden="true">
        <source srcSet={assetUrl(grove, 'avif')} type="image/avif" />
        <img src={assetUrl(grove, 'webp')} alt="" />
      </picture>
      <main className="vb-page">
        <header className="vb-header">
          <button type="button" className="vb-back back-button" ref={back} onClick={onCancel} aria-label={backLabel} title={backLabel}>
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="back-button-label" aria-hidden="true">{backLabel}</span>
          </button>
          <h1 id="vb-title">Make your character</h1>
          <p>Your look. Your place in the valley.</p>
        </header>
        <div className="vb-grid">
          <section className="vb-stage" aria-label="Your character">
            <div className="vb-window">
              <div
                className={paused ? 'vb-preview preview-paused' : 'vb-preview'}
                // eslint-disable-next-line react/no-danger -- see above
                dangerouslySetInnerHTML={{ __html: preview }}
              />
            </div>
            <h2>{who.trim() || 'Your character'}</h2>
            <button type="button" className="vb-ghost" onClick={() => setSpec(random())}>
              ✦ Surprise me
            </button>
            <p className="vb-hint" role="status">
              Choose your look. The valley picks what you are doing each day.
            </p>
            <p className="vb-hint">Preview what you would look like doing each of these.</p>
            <div className="vb-activities" aria-label="Preview activity">
              {PREVIEWS.map((id) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={id === activity}
                  onClick={() => setActivity(id)}
                >
                  {BY_ID[id]?.label ?? id}
                </button>
              ))}
            </div>
            <button type="button" className="vb-ghost vb-motion" onClick={() => setPaused(!paused)}>
              {paused ? 'Play preview' : 'Pause preview'}
            </button>
          </section>

          <div className="vb-editor">
            <nav className="vb-groups" aria-label="Parts of your character">
              {ORDER.map((key) => (
                <button
                  key={key}
                  type="button"
                  className="vb-group"
                  aria-pressed={key === group}
                  onClick={() => setGroup(key)}
                >
                  {WORDS[key]}
                </button>
              ))}
            </nav>

            <section className="vb-card" aria-labelledby="vb-part">
              <div className="vb-section-title">
                <h2 id="vb-part">{WORDS[group]}</h2>
                <span>{counts[group]} choices</span>
              </div>
              {entry.swatches ? (
                <div className="vb-parts swatches">
                  {entry.swatches.map((color, i) => (
                    <button
                      key={i}
                      type="button"
                      className="vb-swatch"
                      aria-label={`${WORDS[group]} ${i + 1}`}
                      aria-pressed={spec[group] === i}
                      style={{ background: color }}
                      onClick={() => setSpec({ ...spec, [group]: i })}
                    />
                  ))}
                </div>
              ) : (
                <div className="vb-parts">
                  {choices!.map(({ label, art }, i) => (
                    <button
                      key={i}
                      type="button"
                      className="vb-choice"
                      aria-pressed={spec[group] === i}
                      aria-label={label}
                      onClick={() => setSpec({ ...spec, [group]: i })}
                    >
                      {/* eslint-disable-next-line react/no-danger -- see above */}
                      <span className="vb-choice-art" dangerouslySetInnerHTML={{ __html: art }} />
                      <span>{label}</span>
                    </button>
                  ))}
                </div>
              )}
            </section>

            <section className="vb-card vb-finish" aria-label="Save your character">
              <label htmlFor="vb-who">Name or nickname</label>
              <p className="vb-small">Use a nickname. <a href="/privacy" target="_blank" rel="noopener noreferrer">How we use your information</a></p>
              <input
                id="vb-who"
                ref={nameField}
                type="text"
                maxLength={24}
                autoComplete="off"
                placeholder="What should we call you?"
                value={who}
                onChange={(event) => setWho(event.target.value)}
              />
              {current && !same(current, spec) ? (
                <div className="vb-existing">
                  <button
                    type="button"
                    className="vb-ghost"
                    onClick={() => {
                      setSpec(current);
                      setSay({ text: 'Loaded the current look.' });
                    }}
                  >
                    Load current look
                  </button>
                </div>
              ) : null}
              <button type="button" className="vb-primary" disabled={busy} onClick={send}>
                {saveLabel}
              </button>
              <p className="vb-small">{note}</p>
              <p className={say.bad ? 'vb-note bad' : 'vb-note'} role="status" aria-live="polite">
                {say.text}
              </p>
            </section>
          </div>
        </div>
      </main>
    </div>,
    document.body,
  );
}
